/**
 * Thin wrappers around the orval-generated training-runs hooks.
 *
 * Run documents are read-only: the training node writes run.json to the
 * data bucket and the API validates it at read time. The launch surface
 * (TRA-1173) adds the launch rows the page polls, the pickers' options and
 * the staff-only launch mutation. Same unwrap pattern as
 * useBenchmarks.ts (orval v8 FETCH client returns {data, status, headers}).
 */

import { useEffect, useState } from 'react'

import {
  type Mutation,
  type QueryClient,
  useMutationState,
  useQueryClient,
} from '@tanstack/react-query'
import type { UseMutationResult } from '@tanstack/react-query'
import { toast } from 'sonner'

import {
  getListTrainingRunsApiTrainingRunsGetQueryKey,
  getListTrainingSetOptionsApiTrainingRunsSetsGetQueryKey,
  type listTrainingRunsApiTrainingRunsGet,
  useGetTrainingGrpoPresetValuesApiTrainingRunsGrpoPresetGet,
  useLaunchTrainingRunApiTrainingRunsLaunchPost,
  useListTrainingGrpoOptionsApiTrainingRunsGrpoGet,
  useListTrainingRunsApiTrainingRunsGet,
  useListTrainingSetOptionsApiTrainingRunsSetsGet,
  useMakeRunDefaultApiTrainingRunsRunIdMakeDefaultPost,
  useRecordTrainingDeploymentApiTrainingRunsRunIdDeploymentPost,
  useReportTrainingRunApiTrainingRunsRunIdReportPost,
  useScoreHeldOutApiTrainingRunsRunIdHeldOutEvalPost,
  useServeTrainingRunApiTrainingRunsRunIdServePost,
  useStopTrainingRunApiTrainingRunsRunIdStopPost,
  useUploadTrainingSetApiTrainingRunsSetsPost,
  getGetTrainingSetRegistrationApiTrainingRunsSetsRegisterSetIdGetQueryKey,
  useGetTrainingSetRegistrationApiTrainingRunsSetsRegisterSetIdGet,
  useRegisterTrainingSetApiTrainingRunsSetsRegisterPost,
  useSignStagingUploadApiTrainingRunsSetsStagingUploadsPost,
  type signStagingUploadApiTrainingRunsSetsStagingUploadsPostResponse,
  getGetHeldOutTestsApiTrainingRunsHeldOutGetQueryKey,
  saveHeldOutTestsApiTrainingRunsHeldOutPut,
  useGetHeldOutTestsApiTrainingRunsHeldOutGet,
  useSaveHeldOutTestsApiTrainingRunsHeldOutPut,
  getGetTrainingScheduleApiTrainingRunsScheduleGetQueryKey,
  saveTrainingScheduleApiTrainingRunsSchedulePut,
  useGetTrainingScheduleApiTrainingRunsScheduleGet,
  useRunTrainingScheduleNowApiTrainingRunsScheduleRunPost,
  useSaveTrainingScheduleApiTrainingRunsSchedulePut,
} from '@/api/generated/training-runs/training-runs'
import { getListModelsApiModelsGetQueryKey } from '@/api/generated/models/models'
import type {
  GrpoRecipeValues,
  HeldOutTestsResponse,
  HeldOutTestsUpdate,
  TrainingDeploymentRecord,
  TrainingGrpoOptionsResponse,
  TrainingLaunchRequest,
  TrainingRunDocument,
  TrainingRunsResponse,
  TrainingScheduleResponse,
  TrainingScheduleUpdate,
  TrainingSetOptionsResponse,
  TrainingSetRegisterRequest,
  TrainingSetRegistration,
  TrainingSetStagingUploadRequest,
  TrainingSetUploadRequest,
} from '@/api/generated/model'
import { type ApiRequestError, getApiErrorDetail, getApiErrorStatus } from '@/api/client'

/** How often the upload dialog asks how a registration from the bucket stands (TRA-1559). */
const REGISTRATION_REFETCH_MS = 3_000

/** Refresh while a run is live so the page tracks it without a reload. */
const LIVE_REFETCH_MS = 60_000
/** A launch waits on a spot node; poll faster so the first status lands. */
const LAUNCH_REFETCH_MS = 15_000
/**
 * How long the page waits for a held-out eval it started. The eval is a
 * Job on the nightly-evals template, which ends within 8 hours
 * (infra/pulumi-v2 nightly-evals-cron.ts DEADLINE_SECONDS): past that no
 * result is coming.
 */
export const HELD_OUT_EVAL_WAIT_MS = 8 * 60 * 60 * 1000
export const SCORE_HELD_OUT_KEY = ['training-runs', 'score-held-out'] as const

/** A held-out eval this page started (TRA-1368). */
export interface StartedHeldOutEval {
  runId: string
  /** When it was started, epoch ms on this browser's clock. */
  startedAt: number
  /**
   * The run's `held_out_eval.scored_at` at that moment, null for a run
   * never scored. The eval's result is the first read that differs.
   */
  scoredAt: string | null
}

/**
 * Whether a started held-out eval is still waited on: the run's result
 * reads as it did at the start, and the Job could still be running.
 * Exported for its test.
 */
export function isAwaited(started: StartedHeldOutEval, scoredAt: string | null, now: number): boolean {
  return started.scoredAt === scoredAt && now - started.startedAt < HELD_OUT_EVAL_WAIT_MS
}

function scoredAtOf(runs: TrainingRunDocument[], runId: string): string | null {
  return runs.find((run) => run.run_id === runId)?.held_out_eval?.scored_at ?? null
}

/**
 * Whether any started held-out eval of these runs is still waited on. The
 * runs are then read again on the live interval, so the eval's score and
 * verdict show without a reload. Exported for its test.
 */
export function awaitsHeldOutEval(
  runs: TrainingRunDocument[],
  started: StartedHeldOutEval[],
  now: number,
): boolean {
  return started.some((one) => isAwaited(one, scoredAtOf(runs, one.runId), now))
}

/**
 * A start as the mutation cache holds it: the run it was asked for, when,
 * and what `scoreHeldOutStart` remembered. Exported for its test.
 */
export function startedHeldOutEval(mutation: Mutation): StartedHeldOutEval {
  const { runId } = mutation.state.variables as { runId: string }
  const { scoredAt } = mutation.state.context as HeldOutEvalStart
  return { runId, startedAt: mutation.state.submittedAt, scoredAt }
}

/**
 * The held-out evals this page started, read off the mutation cache: the
 * start is recorded by `useScoreHeldOutEval`, whichever component it ran in.
 */
function useStartedHeldOutEvals(): StartedHeldOutEval[] {
  return useMutationState({
    filters: { mutationKey: SCORE_HELD_OUT_KEY, status: 'success' },
    select: startedHeldOutEval,
  })
}

/**
 * Until when the page waits for a held-out eval it started of this run and
 * has not read the result of (`scoredAt`, what the run's eval on the page
 * was scored at, is still what it was at the start): the newest such
 * start's Job deadline, epoch ms. Null when there is none. Exported for
 * its test.
 */
export function awaitedUntil(
  started: StartedHeldOutEval[],
  runId: string,
  scoredAt: string | null,
): number | null {
  const starts = started
    .filter((one) => one.runId === runId && one.scoredAt === scoredAt)
    .map((one) => one.startedAt)
  return starts.length === 0 ? null : Math.max(...starts) + HELD_OUT_EVAL_WAIT_MS
}

/**
 * Call `onPassed` once `instant` (epoch ms) passes, at once when it already
 * has. Answers the cancel. Exported for its test.
 */
export function whenPassed(instant: number, onPassed: () => void): () => void {
  const timer = setTimeout(onPassed, Math.max(0, instant - Date.now()))
  return () => {
    clearTimeout(timer)
  }
}

/**
 * Whether `instant` (epoch ms) has passed, with a render when it does:
 * nothing else on the page changes at that moment. The clock is read in
 * the timer, never while rendering.
 */
function useHasPassed(instant: number | null): boolean {
  const [passed, setPassed] = useState<number | null>(null)
  useEffect(() => {
    if (instant === null) return undefined
    return whenPassed(instant, () => {
      setPassed(instant)
    })
  }, [instant])
  return instant !== null && passed === instant
}

/**
 * Whether this page started a held-out eval of the run that it still
 * waits for: no newer result has been read, and the eval's Job could
 * still be running. A Job that died without a result stops being waited
 * for at its deadline, so the run can be scored again.
 */
export function useAwaitsHeldOutEval(runId: string, scoredAt: string | null): boolean {
  const until = awaitedUntil(useStartedHeldOutEvals(), runId, scoredAt)
  const hasPassed = useHasPassed(until)
  return until !== null && !hasPassed
}

interface UnwrappedQuery<TData> {
  data: TData | undefined
  isLoading: boolean
  error: ApiRequestError | null
  isError: boolean
}

export function useTrainingRuns(): UnwrappedQuery<TrainingRunsResponse> {
  const started = useStartedHeldOutEvals()
  const query = useListTrainingRunsApiTrainingRunsGet({
    query: {
      refetchInterval: (current) => {
        const body = current.state.data
        if (body?.status !== 200) return false
        if (body.data.launches.some((launch) => launch.status === 'running')) {
          return LAUNCH_REFETCH_MS
        }
        const live =
          body.data.runs.some((run) => run.status === 'running') ||
          awaitsHeldOutEval(body.data.runs, started, Date.now())
        return live ? LIVE_REFETCH_MS : false
      },
    },
  })
  return {
    data: query.data?.status === 200 ? query.data.data : undefined,
    isLoading: query.isLoading,
    error: query.error ?? null,
    isError: query.isError,
  }
}

/**
 * Keeper sets (TRA-1184) and uploaded sets (TRA-1343). Staff-only; the API
 * 403s everyone else, so callers gate on isEmployee.
 */
export function useTrainingSetOptions(enabled: boolean): UnwrappedQuery<TrainingSetOptionsResponse> {
  const query = useListTrainingSetOptionsApiTrainingRunsSetsGet({ query: { enabled } })
  return {
    data: query.data?.status === 200 ? query.data.data : undefined,
    isLoading: query.isLoading,
    error: query.error ?? null,
    isError: query.isError,
  }
}

/** Staff-only, like the sets: GRPO registry datasets and the default preset (TRA-1377). */
export function useTrainingGrpoOptions(enabled: boolean): UnwrappedQuery<TrainingGrpoOptionsResponse> {
  const query = useListTrainingGrpoOptionsApiTrainingRunsGrpoGet({ query: { enabled } })
  return {
    data: query.data?.status === 200 ? query.data.data : undefined,
    isLoading: query.isLoading,
    error: query.error ?? null,
    isError: query.isError,
  }
}

/**
 * Staff-only: an RL preset's current recipe values at the pinned trainer,
 * which the launch dialog shows in the fields a launch may change. A launch
 * reads the trainer again, so the values are read afresh each time the
 * dialog opens (never from a cache that predates a trainer update); each read
 * goes to the trainer repository, so a failed one is not retried.
 */
export function useTrainingGrpoPresetValues(
  name: string,
  enabled: boolean,
): UnwrappedQuery<GrpoRecipeValues> {
  const query = useGetTrainingGrpoPresetValuesApiTrainingRunsGrpoPresetGet(
    { name },
    { query: { enabled: enabled && name !== '', retry: false, staleTime: 0, refetchOnMount: 'always' } },
  )
  return {
    // Values only from the latest read, and only once it succeeded: while one
    // is in flight, or after one failed, the response kept from an earlier
    // read may predate a trainer update.
    data: query.data?.status === 200 && !query.isFetching && !query.isError ? query.data.data : undefined,
    isLoading: query.isLoading,
    error: query.isFetching ? null : (query.error ?? null),
    isError: query.isError && !query.isFetching,
  }
}

export function useLaunchTrainingRun(): UseMutationResult<
  unknown,
  ApiRequestError,
  { data: TrainingLaunchRequest }
> {
  const queryClient = useQueryClient()
  return useLaunchTrainingRunApiTrainingRunsLaunchPost({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: getListTrainingRunsApiTrainingRunsGetQueryKey(),
        })
      },
      onError: (error) => {
        toast.error(getApiErrorDetail(error) ?? 'Training run was not launched. Try again')
      },
    },
  })
}

/**
 * Staff-only: publish a training and a validation file as a new set of the
 * team (TRA-1343). The dialog shows the api's refusal itself, since it names
 * the file and line at fault, so this hook raises no toast.
 */
export function useUploadTrainingSet(): UseMutationResult<
  unknown,
  ApiRequestError,
  { data: TrainingSetUploadRequest }
> {
  const queryClient = useQueryClient()
  return useUploadTrainingSetApiTrainingRunsSetsPost({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: getListTrainingSetOptionsApiTrainingRunsSetsGetQueryKey(),
        })
      },
    },
  })
}

/**
 * Staff-only: register the files copied into the team's staging folder as
 * a set (TRA-1559). The api answers at once and checks them in the
 * background; the dialog follows useTrainingSetRegistration and shows a
 * refusal itself, so this hook raises no toast.
 */
export function useRegisterTrainingSet(): UseMutationResult<
  unknown,
  ApiRequestError,
  { data: TrainingSetRegisterRequest }
> {
  const queryClient = useQueryClient()
  return useRegisterTrainingSetApiTrainingRunsSetsRegisterPost({
    mutation: {
      // A registration started again after a refusal reads its new state.
      onSuccess: (_answer, variables) => {
        void queryClient.invalidateQueries({
          queryKey: getGetTrainingSetRegistrationApiTrainingRunsSetsRegisterSetIdGetQueryKey(variables.data.set_id),
        })
      },
    },
  })
}

/**
 * How a registration from the bucket stands, polled while the api reads
 * the files. The set list is read again once it is registered.
 */
export function useTrainingSetRegistration(setId: string | null): UnwrappedQuery<TrainingSetRegistration> {
  const queryClient = useQueryClient()
  const query = useGetTrainingSetRegistrationApiTrainingRunsSetsRegisterSetIdGet(setId ?? '', {
    query: {
      enabled: setId !== null,
      refetchInterval: (current) => {
        const body = current.state.data
        if (body?.status !== 200 || body.data.state !== 'checking') return false
        return REGISTRATION_REFETCH_MS
      },
    },
  })
  const registered = query.data?.status === 200 && query.data.data.state === 'registered'
  useEffect(() => {
    if (!registered) return
    void queryClient.invalidateQueries({ queryKey: getListTrainingSetOptionsApiTrainingRunsSetsGetQueryKey() })
  }, [registered, queryClient])
  return {
    data: query.data?.status === 200 ? query.data.data : undefined,
    isLoading: query.isLoading,
    error: query.error ?? null,
    isError: query.isError,
  }
}

/**
 * Staff-only, where the stack signs them: a URL that starts a resumable
 * upload of one file into the team's staging folder (TRA-1559).
 */
export function useSignStagingUpload(): UseMutationResult<
  signStagingUploadApiTrainingRunsSetsStagingUploadsPostResponse,
  ApiRequestError,
  { data: TrainingSetStagingUploadRequest }
> {
  return useSignStagingUploadApiTrainingRunsSetsStagingUploadsPost()
}

/**
 * The team's held-out tests over the tests of its latest scoring night
 * (TRA-1434), read each time the "Held-out tests" dialog opens. Staff only.
 */
export function useHeldOutTests(): UnwrappedQuery<HeldOutTestsResponse> {
  const query = useGetHeldOutTestsApiTrainingRunsHeldOutGet({
    query: { staleTime: 0, refetchOnMount: 'always' },
  })
  return {
    // Tests only from the latest read, and only once it succeeded: a save is
    // built on this list and its revision, so the response kept from an
    // earlier opening is never ticked while a newer one is in flight.
    data: query.data?.status === 200 && !query.isFetching && !query.isError ? query.data.data : undefined,
    isLoading: query.isLoading,
    error: query.error ?? null,
    isError: query.isError,
  }
}

type SavedHeldOutTests = Awaited<ReturnType<typeof saveHeldOutTestsApiTrainingRunsHeldOutPut>>

interface HeldOutSaveCallbacks {
  onSuccess: (saved: SavedHeldOutTests) => void
  onError: (error: ApiRequestError) => void
}

/**
 * What saving the held-out tests does to the cache. A save replaces the
 * whole list and names the revision it was built on, so:
 *
 * - The api answers with the tests as they now read, new revision included,
 *   and that answer becomes the cached read: the next save is built on it.
 * - A 409 means the saved tests changed since this page read them (the api
 *   wrote nothing): reload them, and the dialog starts again from what is
 *   saved now.
 *
 * The staff Jobs rows are refreshed too: a team's "Training dataset" row is
 * blocked until it has held-out tests. Exported for its test.
 */
export function heldOutSaveCallbacks(queryClient: QueryClient): HeldOutSaveCallbacks {
  const key = getGetHeldOutTestsApiTrainingRunsHeldOutGetQueryKey()
  return {
    onSuccess: (saved) => {
      queryClient.setQueryData(key, saved)
      void queryClient.invalidateQueries({
        predicate: ({ queryKey }) => {
          const path = queryKey[0]
          return typeof path === 'string' && /^\/api\/internal\/teams\/[^/]+\/jobs$/.test(path)
        },
      })
    },
    onError: (error) => {
      toast.error(getApiErrorDetail(error) ?? 'The held-out tests were not saved. Try again')
      if (getApiErrorStatus(error) === 409) {
        void queryClient.invalidateQueries({ queryKey: key })
      }
    },
  }
}

/** Replace the team's held-out tests (see heldOutSaveCallbacks). */
export function useSaveHeldOutTests(): UseMutationResult<
  unknown,
  ApiRequestError,
  { data: HeldOutTestsUpdate }
> {
  const queryClient = useQueryClient()
  return useSaveHeldOutTestsApiTrainingRunsHeldOutPut({
    mutation: heldOutSaveCallbacks(queryClient),
  })
}

/**
 * The team's scheduled run, its next tick, where that run starts and the
 * last run it started (TRA-1475), read each time the "Scheduled run" or the
 * "Held-out tests" dialog opens. Staff only.
 */
export function useTrainingSchedule(): UnwrappedQuery<TrainingScheduleResponse> {
  const query = useGetTrainingScheduleApiTrainingRunsScheduleGet({
    query: { staleTime: 0, refetchOnMount: 'always' },
  })
  return {
    // Only the latest read, and only once it succeeded: the form is filled
    // from it, so a schedule kept from an earlier opening never shows while
    // a newer one is in flight.
    data: query.data?.status === 200 && !query.isFetching && !query.isError ? query.data.data : undefined,
    isLoading: query.isLoading,
    error: query.error ?? null,
    isError: query.isError,
  }
}

type SavedTrainingSchedule = Awaited<ReturnType<typeof saveTrainingScheduleApiTrainingRunsSchedulePut>>

interface ScheduleSaveCallbacks {
  onSuccess: (saved: SavedTrainingSchedule) => void
  onError: (error: ApiRequestError) => void
}

/**
 * What saving the scheduled run does to the cache. The api answers with the
 * schedule as it now reads, next tick included, and that answer becomes the
 * cached read, so the form shows what was saved without another request. A
 * 409 can mean someone else saved first: the schedule is read again.
 * Exported for its test.
 */
export function scheduleSaveCallbacks(queryClient: QueryClient): ScheduleSaveCallbacks {
  const key = getGetTrainingScheduleApiTrainingRunsScheduleGetQueryKey()
  return {
    onSuccess: (saved) => {
      queryClient.setQueryData(key, saved)
    },
    onError: (error) => {
      toast.error(getApiErrorDetail(error) ?? 'The scheduled run was not saved. Try again')
      if (getApiErrorStatus(error) === 409) {
        void queryClient.invalidateQueries({ queryKey: key })
      }
    },
  }
}

/** Create or replace the team's scheduled run (see scheduleSaveCallbacks). */
export function useSaveTrainingSchedule(): UseMutationResult<
  unknown,
  ApiRequestError,
  { data: TrainingScheduleUpdate }
> {
  const queryClient = useQueryClient()
  return useSaveTrainingScheduleApiTrainingRunsSchedulePut({
    mutation: scheduleSaveCallbacks(queryClient),
  })
}

/**
 * Start a run of the team's saved schedule now. The launches list is read
 * again so the new run shows on the page, and so is the schedule, whose
 * last run it now is.
 */
export function useRunTrainingScheduleNow(): UseMutationResult<unknown, ApiRequestError, void> {
  const queryClient = useQueryClient()
  return useRunTrainingScheduleNowApiTrainingRunsScheduleRunPost({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: getListTrainingRunsApiTrainingRunsGetQueryKey(),
        })
        void queryClient.invalidateQueries({
          queryKey: getGetTrainingScheduleApiTrainingRunsScheduleGetQueryKey(),
        })
      },
      onError: (error) => {
        toast.error(getApiErrorDetail(error) ?? 'The scheduled run did not start. Try again')
      },
    },
  })
}

/**
 * Staff-only: make a run's served step the team's default model (TRA-1368).
 * The api refuses unless the run's held-out eval found it eligible. A
 * refusal is shown by the confirm dialog that owns the click, so it is
 * not toasted here as well.
 */
export function useMakeRunDefault(): UseMutationResult<unknown, ApiRequestError, { runId: string }> {
  const queryClient = useQueryClient()
  return useMakeRunDefaultApiTrainingRunsRunIdMakeDefaultPost({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: getListModelsApiModelsGetQueryKey() })
        toast.success('This model is now the default. The nightly evals score it from the next night.')
      },
    },
  })
}

/**
 * Staff-only: serve the best step of a grpo run whose own export failed, on
 * a node of its own (a serve launch). The runs are read again so the serve
 * shows under Launches at once. A refusal is shown by the confirm dialog
 * that owns the click, so it is not toasted here as well.
 */
export function useServeTrainingRun(): UseMutationResult<unknown, ApiRequestError, { runId: string }> {
  const queryClient = useQueryClient()
  return useServeTrainingRunApiTrainingRunsRunIdServePost({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: getListTrainingRunsApiTrainingRunsGetQueryKey(),
        })
        toast.success('The serve started. Its progress shows under Launches.')
      },
    },
  })
}

/**
 * Staff-only, on an enterprise install: record an SFT set run's adapter as
 * served by the cluster's vLLM beside its base (TRA-1600). The run's
 * document then carries the deployment, so the runs are refetched.
 */
export function useRecordTrainingDeployment(): UseMutationResult<
  unknown,
  ApiRequestError,
  { runId: string; data: TrainingDeploymentRecord }
> {
  const queryClient = useQueryClient()
  return useRecordTrainingDeploymentApiTrainingRunsRunIdDeploymentPost({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: getListTrainingRunsApiTrainingRunsGetQueryKey(),
        })
        toast.success('The deployment is recorded on the run.')
      },
    },
  })
}

type ListedTrainingRuns = Awaited<ReturnType<typeof listTrainingRunsApiTrainingRunsGet>>

/** What a held-out eval's start remembers: how the run's result read then. */
interface HeldOutEvalStart {
  scoredAt: string | null
}

/**
 * What the cached runs say a run's held-out eval was scored at, as a start
 * records it (see StartedHeldOutEval). Exported for its test.
 */
export function heldOutEvalStart(queryClient: QueryClient, runId: string): HeldOutEvalStart {
  const listed = queryClient.getQueryData<ListedTrainingRuns>(
    getListTrainingRunsApiTrainingRunsGetQueryKey(),
  )
  return { scoredAt: listed?.status === 200 ? scoredAtOf(listed.data.runs, runId) : null }
}

interface ScoreHeldOutStart {
  mutationKey: typeof SCORE_HELD_OUT_KEY
  gcTime: number
  onMutate: (variables: { runId: string }) => HeldOutEvalStart
}

/**
 * How a held-out eval's start is kept: under one key, for as long as its
 * Job can run (a mutation is otherwise dropped five minutes after its
 * button leaves the page), with how the run's result read at the click.
 * Exported for its test.
 */
export function scoreHeldOutStart(queryClient: QueryClient): ScoreHeldOutStart {
  return {
    mutationKey: SCORE_HELD_OUT_KEY,
    gcTime: HELD_OUT_EVAL_WAIT_MS,
    onMutate: ({ runId }) => heldOutEvalStart(queryClient, runId),
  }
}

/**
 * Staff-only: start a held-out eval of a completed run, the first or
 * another (TRA-1368). The start is kept in the mutation cache
 * (`scoreHeldOutStart`), so the runs are polled until the result shows
 * (`useTrainingRuns`), whichever component the button lived in.
 */
export function useScoreHeldOutEval(): UseMutationResult<
  unknown,
  ApiRequestError,
  { runId: string },
  HeldOutEvalStart
> {
  const queryClient = useQueryClient()
  return useScoreHeldOutApiTrainingRunsRunIdHeldOutEvalPost({
    mutation: {
      ...scoreHeldOutStart(queryClient),
      onSuccess: () => {
        toast.success('The held-out eval started.')
      },
      onError: (error) => {
        toast.error(getApiErrorDetail(error) ?? 'The held-out eval did not start. Try again')
      },
    },
  })
}

/** "Report to TrainLoop" on a failed launch: posts the run and its launcher log to #platform. */
export function useReportTrainingRun(): UseMutationResult<
  unknown,
  ApiRequestError,
  { runId: string }
> {
  return useReportTrainingRunApiTrainingRunsRunIdReportPost({
    mutation: {
      onSuccess: () => {
        toast.success('Reported. TrainLoop has the run details and will follow up.')
      },
      onError: (error) => {
        toast.error(getApiErrorDetail(error) ?? 'The report was not sent. Try again')
      },
    },
  })
}

/** Staff-only: stop the running run; the api tears its node down (TRA-1377). */
export function useStopTrainingRun(): UseMutationResult<
  unknown,
  ApiRequestError,
  { runId: string }
> {
  const queryClient = useQueryClient()
  return useStopTrainingRunApiTrainingRunsRunIdStopPost({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: getListTrainingRunsApiTrainingRunsGetQueryKey(),
        })
        toast.success('The run is stopping. Its node shuts down within a few minutes.')
      },
      onError: (error) => {
        toast.error(getApiErrorDetail(error) ?? 'The run was not stopped. Try again')
      },
    },
  })
}
